/**
 * Applying what the inspection said each door and window IS.
 *
 * The inspection (`shared-lib/existing-conditions/openingTargets.js` asks,
 * `scripts/depth_conditions_openings.py` answers) asks every door and window
 * what kind it is, in the same call as its material and condition, and keeps
 * the answer on the doc as `openingType = { claim, verdict, label }`.
 * `openingRowsFromDocs` (below) turns the docs into ROWS; `applyOpeningTypeVerdicts`
 * writes the kind onto the structure's doors and windows. Pure, and in shared-lib
 * because it runs in three places: processing Steps 12b / 17c (so a reprocess
 * keeps the inspected kinds), the inspection-settled trigger (so the kinds land
 * as soon as a run finishes), and the inspection report, which types the plan's
 * doors and windows from the same answers its table reads
 * (`withInspectedOpenings`), so the two can never disagree.
 *
 * Until 2026-10-19 the question was its own LiDAR-processing pass, asking ONE
 * opening per SIZE because each answer cost a room recording download. The
 * inspection downloads every recording anyway, so every opening is asked about
 * and the sharing moved here, to the openings nobody could see.
 *
 * ## Three passes, most specific first
 *
 * 1. **Its own answer.** A measured row is matched back onto the opening it
 *    was asked about by GEOMETRY (`matchesOpening`; ids are re-minted on every
 *    reprocess) and written there.
 * 2. **Same size.** An opening still outstanding — the walkthrough never showed
 *    it, or its answer was a refusal — takes what every measured opening of the
 *    same size AGREES on (`sameProduct`): a house buys its passage doors in a
 *    pack, so the operation and the window style travel within a size.
 * 3. **The house's window line** (windows only). A window with no same-size
 *    answer takes what EVERY measured window agrees on — a house usually buys
 *    one window line, and where its windows disagree it does not have one, so
 *    nothing travels (`agreement` is the intersection, field by field: a
 *    disagreement about the grille costs the grille, not the style).
 *
 * Handing is not measured and nothing here writes it (withdrawn after
 * measurement, `reference-docs/lidar-recon/opening-types.md`). A door's side
 * is kept whatever its kind becomes (`retype`).
 *
 * ## Casement or sliding is settled across the HOUSE, not per window
 *
 * Those two are indistinguishable in an outline, so only visible hardware
 * settles one (`casement_or_slider`, Python side). A window with none in view
 * carries `styleSettled: false`, and `resolveOperableStyle` answers for it:
 * one settled casement anywhere makes the unsettled ones casements, none
 * anywhere falls back to sliding. Positive evidence travels; its absence does not.
 *
 * ## Answers recorded and deliberately not written
 *
 * `bifold` and `no-door` have no home (`DOOR_TYPES` has no bifold, and a cased
 * opening is a change of ELEMENT KIND, not of type). A window the model calls
 * `other` (an awning, a hopper, a transom) is storable as nothing — writing the
 * nearest of the six would print a wrong unit on the window schedule.
 * `slidingPanelCount` stays with the width rule: the model answered 1, 2 and 3
 * across three runs of one patio door.
 */

const {
  DOOR_TYPES,
  isFrenchDoor,
  isGarageDoor,
  isPocketDoor,
  isSlidingDoor,
  isSwingDoor,
} = require('../openings/doorTypes');
const {
  CLAIM_DOOR_TYPE,
  CLAIM_WINDOW_STYLE,
  openingKey,
  matchesOpening,
  sameProduct,
  CLAIM_FOR,
} = require('./openingTargets');

const CHECK = 'opening-type';
const STEP = '12b-opening-type';

/** The six styles `WindowPanel.js` can store. Anything else is not written. */
const STORABLE_WINDOW_STYLES = new Set([
  'double-hung',
  'single-hung',
  'casement',
  'picture',
  'sliding',
  'bay',
]);

const STORABLE_GRILLES = new Set(['none', 'colonial', 'prairie']);

/** Operations with nowhere to be written. Reported, never applied. */
const UNSTORABLE_OPERATIONS = new Set(['bifold', 'no-door']);

const isRightHanded = (doorType) => !!doorType?.startsWith('right');

/** What a hedged answer reads as among its peers: a kind no measured answer can share. */
const HEDGED = '(hedged)';

/** Was this opening seen, but not told apart (hinged or pocket, single- or double-hung)? */
const isHedge = (r) =>
  r?.verdict === 'unverifiable' &&
  r.label &&
  (Array.isArray(r.label.operationCandidates) ||
    Array.isArray(r.label.styleCandidates));

/**
 * The doorType this operation asks for, keeping whatever side the door already
 * carries — its handing came from the swing heuristic, which had the room's
 * geometry; the operation verdict knows nothing about the side. Returns the
 * current type unchanged when the operation already matches.
 */
function retype(currentType, operation) {
  const right = isRightHanded(currentType);
  switch (operation) {
    case 'swing':
      return isSwingDoor(currentType)
        ? currentType
        : right
          ? DOOR_TYPES.RIGHT_INSWING
          : DOOR_TYPES.LEFT_INSWING;
    case 'double-swing':
      if (isFrenchDoor(currentType)) return currentType;
      // An outswing single promoted to a pair stays outswing: the measurement
      // said there are two leaves, not that they now open the other way.
      if (currentType?.includes('Outswing')) {
        return right
          ? DOOR_TYPES.RIGHT_ACTIVE_OUTSWING_FRENCH
          : DOOR_TYPES.LEFT_ACTIVE_OUTSWING_FRENCH;
      }
      return right
        ? DOOR_TYPES.RIGHT_ACTIVE_INSWING_FRENCH
        : DOOR_TYPES.LEFT_ACTIVE_INSWING_FRENCH;
    case 'sliding':
      return isSlidingDoor(currentType)
        ? currentType
        : right
          ? DOOR_TYPES.RIGHT_HANDLE_SLIDING
          : DOOR_TYPES.LEFT_HANDLE_SLIDING;
    case 'pocket':
      return isPocketDoor(currentType)
        ? currentType
        : right
          ? DOOR_TYPES.RIGHT_HANDLE_POCKET
          : DOOR_TYPES.LEFT_HANDLE_POCKET;
    case 'overhead-sectional':
      return isGarageDoor(currentType) ? currentType : DOOR_TYPES.GARAGE;
    default:
      return null;
  }
}

/**
 * The house's answer for a window that is casement-or-sliding with no hardware
 * seen: ONE CRANK ANYWHERE CARRIES THE HOUSE, none anywhere falls back to
 * sliding (user ruling 2026-09-15 — with no hardware in view the model reasons
 * from how the mullion looks, a coin flip that reads as confident).
 */
function resolveOperableStyle(rows) {
  const settledCasement = (rows || []).some(
    (r) =>
      r?.claim === CLAIM_WINDOW_STYLE &&
      r.verdict === 'measured' &&
      r.label?.styleSettled === true &&
      r.label.style === 'casement'
  );
  return settledCasement ? 'casement' : 'sliding';
}

/**
 * Each row's label with its style resolved — the house's answer where its own
 * observations did not settle it. A COPY: the row is the measurement.
 */
function withResolvedStyles(rows) {
  const house = resolveOperableStyle(rows);
  return (rows || []).map((row) => {
    if (row?.claim !== CLAIM_WINDOW_STYLE || !row.label) return row;
    if (row.label.styleSettled !== false) return row;
    return {
      ...row,
      label: { ...row.label, style: house, styleFromHouse: true },
    };
  });
}

/** The window appearance this row asks for, dropping anything unstorable. */
function windowAppearance(label) {
  const out = {};
  if (STORABLE_WINDOW_STYLES.has(label.style)) out.style = label.style;
  if (STORABLE_GRILLES.has(label.grille)) out.grillePattern = label.grille;
  const x = Number(label.litesWide);
  const y = Number(label.litesHigh);
  // A lite count is only meaningful under a grille that draws one, and a zero
  // or a negative is a model slip rather than a measurement.
  if (Number.isInteger(x) && Number.isInteger(y) && x > 0 && y > 0) {
    out.panes = { x, y };
  }
  return out;
}

/**
 * The window fields every row agrees on — the only ones that may travel. With
 * one row this is its own fields; with several, the intersection: two windows
 * that are a casement and a double-hung give no style, and saying nothing is
 * the correct answer there.
 */
function agreement(rows) {
  const labels = rows.map((r) => windowAppearance(r.label));
  if (!labels.length) return { fields: {}, conflicts: [] };
  const agreed = {};
  const conflicts = [];
  for (const field of ['style', 'grillePattern', 'panes']) {
    const present = labels.filter((l) => l[field] !== undefined);
    if (present.length !== labels.length) continue;
    const first = JSON.stringify(present[0][field]);
    if (present.every((l) => JSON.stringify(l[field]) === first)) {
      agreed[field] = present[0][field];
    } else {
      conflicts.push(field);
    }
  }
  return { fields: agreed, conflicts };
}

/** The one operation every door row agrees on, or null. */
function agreedOperation(rows) {
  const ops = new Set(rows.map((r) => r.label?.operation));
  return ops.size === 1 ? [...ops][0] : null;
}

/**
 * Is this opening's kind still a guess? A door whose type was READ from the
 * source (a CAD symbol, `_doorTypeObserved`) is not, nor is one this run
 * already settled (a `scanVerified` stamp of this check). A window is a guess
 * until something writes `category.window.style` — this module or a user.
 */
function isOutstanding(opening, kind) {
  if (
    (opening.scanVerified || []).some(
      (v) => v?.check === CHECK && !v.unresolved
    )
  ) {
    return false;
  }
  if (kind === 'door') return !opening._doorTypeObserved;
  return !opening.category?.window?.style;
}

function stamp(opening, record) {
  if (!Array.isArray(opening.scanVerified)) opening.scanVerified = [];
  opening.scanVerified.push(record);
}

/**
 * Forget what an EARLIER apply settled, so a newer inspection may answer
 * again. A window an earlier apply styled loses its style with its stamp; a
 * user-styled window carries no stamp and keeps its own. A door keeps its
 * type: `retype` reads the side off it, and an operation that still agrees is
 * a confirmation.
 */
function forgetEarlierAnswers(structure) {
  for (const window of structure?.windows || []) {
    const stamped = (window.scanVerified || []).some((v) => v?.check === CHECK);
    if (stamped && window.category?.window) {
      const { style, grillePattern, panes, ...rest } = window.category.window;
      window.category = { ...window.category, window: rest };
    }
  }
  for (const opening of [
    ...(structure?.doors || []),
    ...(structure?.windows || []),
  ]) {
    if (Array.isArray(opening.scanVerified)) {
      opening.scanVerified = opening.scanVerified.filter(
        (v) => v?.check !== CHECK
      );
    }
  }
}

/** The opening `row` was asked about: the nearest outstanding match, or null. */
function ownOpening(row, pool, kind) {
  const candidates = pool.filter(
    (o) => isOutstanding(o, kind) && matchesOpening(row.openingKey, o)
  );
  const c = row.openingKey?.center || {};
  const dist = (o) => {
    const k = openingKey(o).center;
    return Math.hypot(k.x - (c.x ?? 0), k.z - (c.z ?? 0));
  };
  return candidates.sort((a, b) => dist(a) - dist(b))[0] || null;
}

const sizeOf = (opening) => ({
  widthM: opening.dimensions?.x,
  heightM: opening.dimensions?.y,
});
const rowSize = (row) => ({
  widthM: row.openingKey?.widthM,
  heightM: row.openingKey?.heightM,
});

/**
 * Write every measured opening-type answer onto the structure.
 *
 * @param {Object} args
 * @param {Object} args.structure - mutated in place: `{doors, windows}`
 * @param {Object[]} [args.rows] - `inspected-opening-types` rows
 * @param {boolean} [args.fresh] - forget what an earlier apply settled first
 *   (the trigger, applying a newer inspection over a structure a reprocess
 *   already typed); processing builds a fresh structure and leaves it false
 * @returns {{applied: Object[], skipped: Object[], stats: Object}}
 */
function applyOpeningTypeVerdicts({ structure, rows, fresh = false }) {
  const applied = [];
  const skipped = [];
  const stats = {
    rowsRead: 0,
    doorsRetyped: 0,
    windowsStyled: 0,
    confirmations: 0,
    unstorable: 0,
    unmatched: 0,
  };
  if (fresh) forgetEarlierAnswers(structure);
  const measured = withResolvedStyles(
    (rows || []).filter((r) => r?.verdict === 'measured' && r.label)
  );
  const doors = structure?.doors || [];
  const windows = structure?.windows || [];
  const ctx = { applied, skipped, stats };
  // An opening that was SEEN but could not be told apart — hinged or pocket,
  // single- or double-hung — keeps its type: it never borrows its size's or the
  // house's answer, and it stands among its peers as a disagreement, so neither
  // does any opening beside it. Without this a house of hedged hung windows and
  // one measured slider gave every window "sliding" (Talkeetna, 2026-10-21).
  const hedges = (rows || []).filter(isHedge);
  const heldBack = new Set();
  for (const row of hedges) {
    const isDoor = row.claim === CLAIM_DOOR_TYPE;
    const own = ownOpening(
      row,
      isDoor ? doors : windows,
      isDoor ? 'door' : 'window'
    );
    if (own) heldBack.add(own);
  }
  const doorRows = measured.filter((r) => r.claim === CLAIM_DOOR_TYPE);
  const windowRows = measured.filter((r) => r.claim === CLAIM_WINDOW_STYLE);
  const doorPeers = [
    ...doorRows,
    ...hedges
      .filter((r) => r.claim === CLAIM_DOOR_TYPE)
      .map((r) => ({ ...r, label: { operation: HEDGED } })),
  ];
  const windowPeers = [
    ...windowRows,
    ...hedges
      .filter((r) => r.claim === CLAIM_WINDOW_STYLE)
      .map((r) => ({ ...r, label: { style: HEDGED } })),
  ];
  const mayBorrow = (opening, kind) =>
    isOutstanding(opening, kind) && !heldBack.has(opening);

  // 1. Each opening its own answer.
  for (const row of measured) {
    stats.rowsRead += 1;
    const isDoor = row.claim === CLAIM_DOOR_TYPE;
    const own = ownOpening(
      row,
      isDoor ? doors : windows,
      isDoor ? 'door' : 'window'
    );
    if (!own) {
      stats.unmatched += 1;
      skipped.push({ row, reason: 'the opening it was asked about is gone' });
      continue;
    }
    if (isDoor) {
      writeDoor(own, row.label.operation, { row, observed: 'operation' }, ctx);
    } else {
      writeWindow(
        own,
        windowAppearance(row.label),
        {
          row,
          observed: row.label.styleFromHouse ? 'style-from-house' : 'style',
        },
        ctx
      );
    }
  }

  // 2. An opening nobody saw takes what its size agrees on.
  for (const door of doors.filter((d) => mayBorrow(d, 'door'))) {
    const peers = doorPeers.filter((r) =>
      sameProduct(sizeOf(door), rowSize(r))
    );
    const operation = peers.length ? agreedOperation(peers) : null;
    if (!operation || operation === HEDGED) continue;
    writeDoor(
      door,
      operation,
      { viaOpeningIds: peers.map((r) => r.openingId), observed: 'same-size' },
      ctx
    );
  }
  for (const window of windows.filter((w) => mayBorrow(w, 'window'))) {
    const peers = windowPeers.filter((r) =>
      sameProduct(sizeOf(window), rowSize(r))
    );
    if (!peers.length) continue;
    const { fields } = agreement(peers);
    if (!fields.style) continue;
    writeWindow(
      window,
      fields,
      { viaOpeningIds: peers.map((r) => r.openingId), observed: 'same-size' },
      ctx
    );
  }

  // 3. A window with no same-size answer takes the house's window line.
  const outstanding = windows.filter((w) => mayBorrow(w, 'window'));
  if (outstanding.length && windowRows.length) {
    const { fields, conflicts } = agreement(windowPeers);
    if (conflicts.length) {
      stats.houseConflicts = conflicts;
      skipped.push({
        row: windowRows[0],
        reason:
          `the measured windows disagree on ${conflicts.join(', ')} — this ` +
          'house does not have one window line, so those fields do not travel',
      });
    }
    if (fields.style) {
      for (const window of outstanding) {
        writeWindow(
          window,
          fields,
          {
            viaOpeningIds: windowRows.map((r) => r.openingId),
            observed: 'general-style',
          },
          ctx
        );
      }
    }
  }
  return { applied, skipped, stats };
}

/** One door, retyped to `operation` (or withheld when it has nowhere to go). */
function writeDoor(door, operation, provenance, { applied, skipped, stats }) {
  if (UNSTORABLE_OPERATIONS.has(operation)) {
    if (provenance.row) {
      stats.unstorable += 1;
      skipped.push({
        row: provenance.row,
        reason:
          operation === 'no-door'
            ? 'measured as a cased opening — changing a door into one is a change of ' +
              'element kind, not of type, and this step only types'
            : 'measured as a bifold, which DOOR_TYPES has no value for',
      });
    }
    return;
  }
  const current = door.category?.door?.doorType || null;
  const next = retype(current, operation);
  if (!next) return;
  if (next === current) {
    stats.confirmations += 1;
  } else {
    door.category = door.category || {};
    door.category.door = { ...door.category.door, doorType: next };
    stats.doorsRetyped += 1;
  }
  stamp(door, record(CLAIM_DOOR_TYPE, next === current, provenance));
  applied.push({
    openingId: door.id,
    kind: 'door',
    from: current,
    to: next,
    subject: Boolean(provenance.row),
    observed: provenance.observed,
  });
}

/** One window, given `appearance` (or withheld when its style is not storable). */
function writeWindow(window, appearance, provenance, ctx) {
  const { applied, skipped, stats } = ctx;
  if (!appearance.style) {
    if (provenance.row) {
      stats.unstorable += 1;
      skipped.push({
        row: provenance.row,
        reason: `measured as "${provenance.row.label.style}", which the app has no style for`,
      });
    }
    return;
  }
  const current = window.category?.window?.style || null;
  window.category = window.category || {};
  window.category.window = { ...window.category.window, ...appearance };
  if (current === appearance.style) stats.confirmations += 1;
  else stats.windowsStyled += 1;
  stamp(
    window,
    record(CLAIM_WINDOW_STYLE, current === appearance.style, provenance)
  );
  applied.push({
    openingId: window.id,
    kind: 'window',
    from: current,
    to: appearance.style,
    subject: Boolean(provenance.row),
    observed: provenance.observed,
  });
}

/** The `scanVerified` stamp: what was settled, and which opening was SEEN to settle it. */
function record(claim, agreed, { row, viaOpeningIds, observed }) {
  return {
    check: CHECK,
    step: STEP,
    claim,
    verdict: agreed ? 'confirmed' : 'corrected',
    observed,
    ...(row?.runId ? { runId: row.runId } : {}),
    ...(viaOpeningIds ? { viaOpeningIds } : {}),
  };
}

const OPENING_KINDS = Object.keys(CLAIM_FOR);

const timeOf = (doc) => Date.parse(doc.measuredAt || '') || 0;

/**
 * A doc's rows: one, or one per door of a UNIT the inspection asked about whole
 * (`anchor.members`, a pair under one casing RoomPlan recorded as two doors). A
 * unit's answer is spread onto each door — except that a hinged PAIR is a single
 * hinged leaf in each half, so each half takes `swing`, never a French pair of
 * its own.
 */
function rowsOfDoc(d) {
  const base = {
    claim: d.openingType.claim,
    verdict: d.openingType.verdict,
    label: d.openingType.label || null,
    openingId: d.elementId || null,
    targetId: d.targetId || d.id || null,
    runId: d.runId || null,
    measuredAt: d.measuredAt || null,
  };
  const members = d.anchor.members;
  if (!Array.isArray(members) || members.length < 2) {
    return [{ ...base, openingKey: d.anchor.openingKey }];
  }
  const label =
    base.label && base.label.operation === 'double-swing'
      ? { ...base.label, operation: 'swing', operationOfUnit: 'double-swing' }
      : base.label;
  return members.map((key) => ({
    ...base,
    label,
    openingKey: key,
    unitKey: d.anchor.openingKey,
  }));
}

/**
 * Rows from existing-conditions docs, newest per opening. Pure; docs of other
 * kinds and docs with no type answer are ignored.
 */
function openingRowsFromDocs(docs) {
  const rows = (docs || [])
    .filter(
      (d) =>
        d &&
        OPENING_KINDS.includes(d.kind) &&
        d.openingType &&
        d.openingType.claim === CLAIM_FOR[d.kind] &&
        d.anchor?.openingKey
    )
    .sort((a, b) => timeOf(b) - timeOf(a))
    .flatMap(rowsOfDoc);
  const kept = [];
  for (const row of rows) {
    // a key against itself as an opening: same centre and size within noise
    const asOpening = {
      center: row.openingKey.center,
      dimensions: { x: row.openingKey.widthM, y: row.openingKey.heightM },
      rotation: { y: row.openingKey.rotationY },
    };
    const newer = kept.some(
      (k) => k.claim === row.claim && matchesOpening(k.openingKey, asOpening)
    );
    if (!newer) kept.push(row);
  }
  return kept;
}

/** The key a structure keeps a kind of opening under: `doors`, or `structureDoors`. */
function openingKeyOf(structure, kind) {
  const prefixed = `structure${kind.charAt(0).toUpperCase()}${kind.slice(1)}`;
  return Array.isArray(structure[prefixed]) ? prefixed : kind;
}

/**
 * A structure's doors and windows with the inspected kinds written, as COPIES,
 * and what changed (`result`). `fresh`: an earlier inspection's answers are
 * forgotten first, so this one answers again. The settle trigger writes these;
 * the report draws them.
 */
function typedOpenings(structure, rows) {
  const copy = {
    doors: JSON.parse(
      JSON.stringify(structure[openingKeyOf(structure, 'doors')] || [])
    ),
    windows: JSON.parse(
      JSON.stringify(structure[openingKeyOf(structure, 'windows')] || [])
    ),
  };
  const result = applyOpeningTypeVerdicts({
    structure: copy,
    rows,
    fresh: true,
  });
  return { ...copy, result };
}

/**
 * The structure a report's plans draw: as found, with its doors and windows
 * typed from the SAME inspection answers the report's table reads (`docs`), so
 * the plan cannot draw a swing arc beside a row saying "Pocket doors" (the user,
 * 2026-10-20). Worked out here rather than read from the "Inspection Complete"
 * checkpoint, which exists only once the settle trigger has run. Pure; the input
 * is not changed.
 */
function withInspectedOpenings(structure, docs) {
  if (!structure) return structure;
  const rows = openingRowsFromDocs(docs);
  if (!rows.length) return structure;
  const typed = typedOpenings(structure, rows);
  return {
    ...structure,
    [openingKeyOf(structure, 'doors')]: typed.doors,
    [openingKeyOf(structure, 'windows')]: typed.windows,
  };
}

module.exports = {
  CHECK,
  openingRowsFromDocs,
  typedOpenings,
  withInspectedOpenings,
  STEP,
  applyOpeningTypeVerdicts,
  retype,
  windowAppearance,
  agreement,
  resolveOperableStyle,
  withResolvedStyles,
  isOutstanding,
  STORABLE_WINDOW_STYLES,
  UNSTORABLE_OPERATIONS,
};
